import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '../../SiteSettings';
import './Loader.css';

/**
 * Shared loading indicator.
 *
 * - Shows the clinic logo (from Settings) with a gentle pulse, an animated
 *   progress bar and a status line describing what is being loaded.
 * - Never shows a broken image: if the logo is missing or fails, the brand
 *   wordmark is used instead.
 * - `status` is the human-readable line under the animation
 *   (e.g. "Loading services", "Saving doctor").
 *
 * Variants:
 *   default — full-size block loader used by public pages.
 *   inline  — smaller, for in-panel admin tables/lists.
 *
 * Usage:
 *   <Loader status="Loading services" />
 *   <Loader inline status="Loading leads" />
 */
export default function Loader({ status = 'Loading', inline = false }) {
  const { logoUrl } = useSiteSettings();
  const { pathname } = useLocation();
  const isPublic = !pathname.startsWith('/admin');
  const brand = isPublic ? 'Amaanat IVF' : 'Admin';

  return (
    <div
      className={`loader ${inline ? 'loader-inline' : ''} ${
        isPublic ? '' : 'loader-admin'
      }`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="loader-logo-wrap">
        {logoUrl ? (
          <img
            className="loader-logo"
            src={logoUrl}
            alt=""
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.add('visible');
            }}
          />
        ) : null}
        <span className={`loader-brand ${logoUrl ? '' : 'visible'}`}>
          {brand}
        </span>
      </div>

      <div className="loader-bar" aria-hidden="true">
        <span className="loader-bar-fill" />
      </div>

      <p className="loader-status">{status}</p>
    </div>
  );
}
